import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  TextField,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  FlightTakeoff as FlightIcon,
  Description as DocumentIcon,
  Checkroom as ClothesIcon,
  Devices as ElectronicsIcon,
  HealthAndSafety as HealthIcon,
  EventAvailable as DepartureIcon,
  MoreVert as MoreVertIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import { DOCUMENT, documentAddButtonSx, documentEyebrowSx, documentTextButtonSx } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';

const CATEGORY_CONFIG = {
  booking: {
    icon: <FlightIcon sx={{ fontSize: 18 }} />,
    color: '#1976d2',
    labelKey: 'checklist.categories.booking',
  },
  documents: {
    icon: <DocumentIcon sx={{ fontSize: 18 }} />,
    color: '#e65100',
    labelKey: 'checklist.categories.documents',
  },
  packing_clothes: {
    icon: <ClothesIcon sx={{ fontSize: 18 }} />,
    color: '#7b1fa2',
    labelKey: 'checklist.categories.packingClothes',
  },
  packing_electronics: {
    icon: <ElectronicsIcon sx={{ fontSize: 18 }} />,
    color: '#0097a7',
    labelKey: 'checklist.categories.packingElectronics',
  },
  packing_health: {
    icon: <HealthIcon sx={{ fontSize: 18 }} />,
    color: '#2e7d32',
    labelKey: 'checklist.categories.packingHealth',
  },
  before_departure: {
    icon: <DepartureIcon sx={{ fontSize: 18 }} />,
    color: '#c62828',
    labelKey: 'checklist.categories.beforeDeparture',
  },
  general: {
    icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
    color: '#546e7a',
    labelKey: 'checklist.categories.general',
  },
};

const CATEGORY_ORDER = ['booking', 'documents', 'packing_clothes', 'packing_electronics', 'packing_health', 'before_departure', 'general'];

const TripChecklist = ({ tripId, isPartner = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const requestConfig = useMemo(() => (isPartner ? { usePartnerAuth: true } : undefined), [isPartner]);
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState({ total: 0, checked: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [newItemText, setNewItemText] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('general');
  const [newItemVisibility, setNewItemVisibility] = useState('shared');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  const fetchChecklist = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/checklist/${tripId}`, requestConfig);
      setItems(response.data.items);
      setProgress(response.data.progress);

      // Auto-expand all categories on first load
      const categories = {};
      response.data.items.forEach(item => {
        categories[item.category] = true;
      });
      setExpandedCategories(prev => {
        if (Object.keys(prev).length === 0) return categories;
        return prev;
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(t('checklist.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [tripId, t, requestConfig]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  const handleToggle = async (itemId) => {
    try {
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, is_checked: !item.is_checked } : item
      ));

      const response = await api.put(`/checklist/${tripId}/${itemId}/toggle`, undefined, requestConfig);

      setItems(prev => prev.map(item =>
        item.id === itemId ? response.data : item
      ));

      setItems(prev => {
        const total = prev.length;
        const checked = prev.filter(i => i.is_checked).length;
        setProgress({ total, checked, percentage: total > 0 ? Math.round((checked / total) * 100) : 0 });
        return prev;
      });
    } catch (err) {
      console.error('Error toggling item:', err);
      fetchChecklist();
    }
  };

  const handleAddItem = async () => {
    if (!newItemText.trim()) return;

    try {
      const response = await api.post(`/checklist/${tripId}`, {
        text: newItemText.trim(),
        category: newItemCategory,
        visibility: newItemVisibility,
      }, requestConfig);

      setItems(prev => [...prev, response.data]);
      setProgress(prev => ({
        total: prev.total + 1,
        checked: prev.checked,
        percentage: prev.total + 1 > 0 ? Math.round((prev.checked / (prev.total + 1)) * 100) : 0,
      }));
      setNewItemText('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding item:', err);
      setError(t('checklist.addError'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/checklist/${tripId}/${itemId}`, requestConfig);
      setItems(prev => prev.filter(item => item.id !== itemId));
      setProgress(prev => {
        const newTotal = prev.total - 1;
        const wasChecked = items.find(i => i.id === itemId)?.is_checked;
        const newChecked = wasChecked ? prev.checked - 1 : prev.checked;
        return {
          total: newTotal,
          checked: newChecked,
          percentage: newTotal > 0 ? Math.round((newChecked / newTotal) * 100) : 0,
        };
      });
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(t('checklist.deleteError'));
    }
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const handleEditItem = async (itemId) => {
    if (!editText.trim()) return;

    try {
      const response = await api.put(`/checklist/${tripId}/${itemId}`, {
        text: editText.trim(),
      }, requestConfig);
      setItems(prev => prev.map(item =>
        item.id === itemId ? response.data : item
      ));
      setEditingItem(null);
      setEditText('');
    } catch (err) {
      console.error('Error editing item:', err);
      setError(t('checklist.editError'));
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const sortedCategories = CATEGORY_ORDER.filter(cat => groupedItems[cat]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={8} sx={{ mb: 3, borderRadius: 1 }} />
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  const addForm = showAddForm ? (
        <Box
          sx={{
            mt: 2.25,
            p: 2,
            bgcolor: DOCUMENT.surface,
            border: `1px solid ${DOCUMENT.cardRule}`,
          }}
        >
          <Typography sx={{ mb: 1.5, fontFamily: DOCUMENT.display, fontSize: 18 }}>
            {t('checklist.addNewItem')}
          </Typography>
          <TextField
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            placeholder={t('checklist.itemPlaceholder')}
            size="small"
            fullWidth
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddItem();
              if (e.key === 'Escape') { setShowAddForm(false); setNewItemText(''); }
            }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {CATEGORY_ORDER.map(cat => {
              const catConfig = CATEGORY_CONFIG[cat];
              const selected = newItemCategory === cat;
              return (
                <Chip
                  key={cat}
                  label={t(catConfig.labelKey)}
                  size="small"
                  variant="outlined"
                  onClick={() => setNewItemCategory(cat)}
                  sx={{
                    fontSize: '0.7rem',
                    borderRadius: 0,
                    borderColor: selected ? DOCUMENT.accent : DOCUMENT.ruleHard,
                    color: selected ? DOCUMENT.accent : DOCUMENT.ink2,
                    bgcolor: 'transparent',
                  }}
                />
              );
            })}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={newItemVisibility === 'personal'}
                onChange={(e) => setNewItemVisibility(e.target.checked ? 'personal' : 'shared')}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: DOCUMENT.accent },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: DOCUMENT.accent },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: DOCUMENT.ink2 }}>
                {newItemVisibility === 'personal' ? t('checklist.personalOnly') : t('checklist.sharedWithAll')}
              </Typography>
            }
            sx={{ mb: 1.5 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button size="small" onClick={() => { setShowAddForm(false); setNewItemText(''); }} sx={documentTextButtonSx}>
              {t('common.cancel')}
            </Button>
            <Button
              size="small"
              onClick={handleAddItem}
              disabled={!newItemText.trim()}
              sx={{ ...documentAddButtonSx, mt: 0 }}
            >
              {t('checklist.addItem')}
            </Button>
          </Box>
        </Box>
  ) : null;

  return (
    <Box>
      <DocumentSectionHead
        title={t('tripTabs.checklist')}
        meta={t('tripTabs.packedCount', { checked: progress.checked, total: progress.total })}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {sortedCategories.map(category => {
        const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
        const categoryItems = groupedItems[category];
        const isExpanded = expandedCategories[category] !== false;

        return (
          <Box key={category} sx={{ mb: 3.5 }}>
            <Box
              onClick={() => toggleCategory(category)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
            >
              <Typography sx={documentEyebrowSx}>
                {t(config.labelKey)}
              </Typography>
              <Box sx={{ ml: 'auto', color: DOCUMENT.ink3 }}>
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
            </Box>

            <Collapse in={isExpanded}>
              {categoryItems.map(item => {
                const isGroupItem = Boolean(Number(item.group_item));
                let whoLabel = item.checked_by_name || t('checklist.shared');
                if (item.visibility === 'personal') whoLabel = t('checklist.personal');
                if (isGroupItem) whoLabel = t('checklist.fromOrganizer');

                return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.35,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                    fontSize: '0.90625rem',
                    color: item.is_checked ? DOCUMENT.ink3 : DOCUMENT.ink,
                    textDecoration: item.is_checked ? 'line-through' : 'none',
                  }}
                >
                  <Checkbox
                    checked={!!item.is_checked}
                    onChange={() => handleToggle(item.id)}
                    size="small"
                    sx={{
                      p: 0,
                      color: DOCUMENT.accent,
                      '&.Mui-checked': { color: DOCUMENT.accent },
                      '& .MuiSvgIcon-root': { fontSize: 16 },
                    }}
                  />

                  {editingItem === item.id ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditItem(item.id);
                          if (e.key === 'Escape') { setEditingItem(null); setEditText(''); }
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }}
                      />
                      <IconButton size="small" onClick={() => handleEditItem(item.id)}>
                        <SaveIcon sx={{ fontSize: 18, color: DOCUMENT.accent }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setEditingItem(null); setEditText(''); }}>
                        <CloseIcon sx={{ fontSize: 18, color: DOCUMENT.ink3 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: '0.90625rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.text}
                    </Typography>
                  )}

                  {editingItem !== item.id && (
                    <>
                      <Typography
                        data-testid={isGroupItem ? `checklist-group-item-${item.id}` : undefined}
                        sx={{ ml: 'auto', fontSize: 11, color: DOCUMENT.ink3, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}
                      >
                        {whoLabel}
                      </Typography>
                      {!isGroupItem && (
                        <IconButton
                          size="small"
                          aria-label={t('common.edit')}
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuItem(item);
                          }}
                          sx={{ color: DOCUMENT.ink3, opacity: 0.7, '&:hover': { opacity: 1 } }}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
                );
              })}
            </Collapse>
          </Box>
        );
      })}

      {!showAddForm ? (
        <Button onClick={() => setShowAddForm(true)} sx={documentAddButtonSx}>
          {t('checklist.addItem')}
        </Button>
      ) : addForm}

      {/* Item Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuItem(null); }}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 180 },
        }}
      >
        <MenuItem onClick={() => {
          setEditingItem(menuItem.id);
          setEditText(menuItem.text);
          setMenuAnchor(null);
          setMenuItem(null);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteItem(menuItem?.id)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TripChecklist;
